<template>
	<v-card tile elevation="0" style="height: 100vh; overflow-y: hidden">
		<v-card-title class="card-header text-left fixed-card-title2">
			<v-row no-gutters>
				<v-col cols="10">
					<v-toolbar-title>{{ $t('edit-functional-status-header') }}</v-toolbar-title>
				</v-col>
				<v-spacer />
				<v-icon @click="closeDrawer" class="white-icon">mdi-close</v-icon>
			</v-row>
		</v-card-title>
		<v-card-text class="overflow-auto pa-4" style="height: calc(100vh - 205px)" v-if="!loading">
			<v-expansion-panels style="max-height: 57vh" multiple v-model="panels">
				<v-expansion-panel v-for="panel in workingFunctionalStatusData" :key="panel.category">
					<v-expansion-panel-header class="pa-2">
						<template v-slot:actions>
							<v-icon color="primary">mdi-menu-down</v-icon>
						</template>
						<span class="text-left text-body-1">{{ $t(panel.name) }}</span>
					</v-expansion-panel-header>

					<v-expansion-panel-content class="no-padding-expansion-panel">
						<v-divider class="my-2" />
						<v-row dense class="ma-0 pa-4 pt-0">
							<v-col cols="12" class="pa-0" v-if="panel.category === 'ADLs' || panel.category === 'IADLs'">
								<v-row class="ma-0">
									<v-checkbox
										v-model="panel.independent"
										color="primary"
										:label="$t('independent')"
										@change="flipIndependence(panel.category)"
										hide-details
										class="ma-0 px-0 py-2" />
								</v-row>

								<v-row dense class="ma-0">
									<v-col cols="12" class="pa-0">
										<p class="text-left ma-0 mb-2">{{ $t('assistance-needed') }}:</p>
										<v-row no-gutters>
											<v-col cols="12" class="pa-0">
												<v-row v-for="item in panel.items" :key="item.id" no-gutters>
													<v-checkbox
														v-model="item.flag"
														color="primary"
														:label="$t(item.value)"
														:disabled="panel.independent"
														hide-details
														class="ma-0 px-0 py-2" />
												</v-row>
											</v-col>
										</v-row>
									</v-col>
								</v-row>
							</v-col>
							<v-col v-else>
								<v-row dense ma-0>
									<v-col cols="12" class="pa-0">
										<p class="text-left ma-0 mb-2">{{ $t('status') }}:</p>
										<v-row no-gutters>
											<v-col cols="12" class="pa-0">
												<v-row v-for="item in panel.items" :key="item.id" no-gutters>
													<v-checkbox
														v-model="item.flag"
														color="primary"
														:label="$t(item.value)"
														hide-details
														class="ma-0 px-0 py-2" />
												</v-row>
											</v-col>
										</v-row>
									</v-col>
								</v-row>
							</v-col>
						</v-row>
					</v-expansion-panel-content>
				</v-expansion-panel>
			</v-expansion-panels>
		</v-card-text>
		<v-card-text class="overflow-auto pa-4" style="height: calc(100vh - 205px)" v-else />
		<v-container class="px-4">
			<v-row dense class="ma-0">
				<v-col>
					<InputTimeFormatter
						:inputValue="timeSpentSeconds"
						@update:inputValue="(val) => (timeSpentSeconds = val)"
						:inputLabel="$t('time-spent')" />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="6" order="1">
					<v-btn @click="closeDrawer()" color="secondary" style="color: black" block>
						{{ $t('cancel') }}
					</v-btn>
				</v-col>
				<v-tooltip left v-if="!timeSpentSeconds || timeSpentSeconds > 7200">
					<template v-slot:activator="{ on, attrs }">
						<v-col cols="6" v-bind="attrs" v-on="on" order="2">
							<v-btn disabled block>{{ $t('update') }}</v-btn>
						</v-col>
					</template>
					{{ $t('fill-all-fields-warning') }}
				</v-tooltip>
				<v-col cols="6" v-else order="2">
					<v-btn @click="editFunctionalStatus()" color="dark_purple" style="color: white" block>
						{{ $t('update') }}
					</v-btn>
				</v-col>
			</v-row>
		</v-container>
	</v-card>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import InputTimeFormatter from '../Client/components/InputTimeFormatter.vue';
export default {
	name: 'CCMFunctionalStatusEdit',
	components: { InputTimeFormatter },
	data () {
		return {
			panels: [0, 1, 2, 3, 4, 5, 6],
			timeSpentSeconds: 0,
			workingFunctionalStatusData: null,
			functionalStatusData: null,
			loading: true,
		};
	},
	computed: {
		...mapState({
			currentRecord: (state) => state.ccmRecords.ccmRecord,
			userData: (state) => state.authentication.userData,
		}),
		...mapGetters({ userFullName: 'authentication/getFullName' }),
	},

	methods: {
		...mapActions({
			updateFunctionalStatus: 'ccmRecords/updateFunctionalStatus',
			getFunctionalStatusTemplate: 'ccmRecords/getFunctionalStatusTemplate',
		}),

		closeDrawer () {
			this.timeSpentSeconds = 0;
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', false);
			this.$store.commit('SET_EDIT_DETAILS_DRAWER', false);
		},

		flipIndependence (category) {
			this.workingFunctionalStatusData = this.workingFunctionalStatusData.map((f) => {
				if (f.category === category) {
					return {
						...f,
						items: !f.independent ? f.items : f.items.map((item) => ({ ...item, flag: false })),
					};
				}
				return f;
			});
		},

		async getFunctionalStatusData () {
			let rawData;
			try {
				rawData = JSON.parse(this.currentRecord.functionalStatus);
			} catch (e) {
				rawData = null;
			}
			if (!rawData) {
				const response = await this.getFunctionalStatusTemplate();
				rawData = response.data;
				rawData[0].independent = true;
				rawData[0].dependentOnAll = false;
				rawData[1].independent = true;
				rawData[1].dependentOnAll = false;
			}
			return rawData;
		},

		editFunctionalStatus () {
			this.workingFunctionalStatusData[0].independent =
				this.workingFunctionalStatusData[0].independent ||
				Object.values(this.workingFunctionalStatusData[0].items).every((v) => v.flag === false);
			this.workingFunctionalStatusData[1].independent =
				this.workingFunctionalStatusData[1].independent ||
				Object.values(this.workingFunctionalStatusData[1].items).every((v) => v.flag === false);
			const functionalStatus = {
				id: this.currentRecord.id,
				body: {
					hospitalId: this.currentRecord.hospitalId,
					providerId: this.currentRecord.providerId,
					coordinatorId: this.currentRecord.coordinatorId,
					functionalStatus: JSON.stringify(this.workingFunctionalStatusData),
					lastModifiedBy: `${this.userData.firstName} ${this.userData.lastName}`,
					timeSpentSeconds: this.timeSpentSeconds,
				},
			};
			this.$store.commit('ccmRecords/UPDATE_CCM_RECORD', {
				functionalStatus: JSON.stringify(this.workingFunctionalStatusData),
			});
			this.updateFunctionalStatus(functionalStatus);
			this.closeDrawer();
		},
	},

	async mounted () {
		this.workingFunctionalStatusData = await this.getFunctionalStatusData();
		this.loading = false;
	},

	watch: {
		currentRecord: {
			async handler () {
				this.loading = true;
				this.functionalStatusData = await this.getFunctionalStatusData();
				this.loading = false;
			},
			deep: true,
			immediate: true,
		},
	},
};
</script>

<style>
.no-padding-expansion-panel > .v-expansion-panel-content__wrap {
	padding: 0px !important;
}
</style>
